import { apiService } from './api';
import {
  GuestBlogSite,
  CreateGuestBlogSiteRequest,
  UpdateGuestBlogSiteRequest,
  GuestBlogSiteFilters,
  SetPriceOverrideRequest,
} from '../types/guestBlogSite';

export interface CatalogGateMeta {
  gated: boolean;
  enabled?: boolean;
  catalogGateApplies?: boolean;
  previewLimit: number;
  unlockMinAmount: number;
  walletBalance?: number;
  remainingToUnlock?: number;
  unlocked?: boolean;
  canOrder?: boolean;
  addFundsPath?: string;
  reason?: string | null;
  teaserCount?: number;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  catalogGate?: CatalogGateMeta;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

class GuestBlogSiteService {
  private baseUrl = '/guest-sites';

  async getGuestBlogSites(filters: GuestBlogSiteFilters = {}): Promise<PaginatedResponse<GuestBlogSite>> {
    const params = new URLSearchParams();
    
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.category) params.append('category', filters.category);
    if (filters.country) params.append('country', filters.country);
    if (filters.siteLanguage) params.append('siteLanguage', filters.siteLanguage);
    if (filters.status) params.append('status', filters.status);
    if (filters.publisherId) params.append('publisherId', filters.publisherId);
    if (filters.outreachEmail) params.append('outreachEmail', filters.outreachEmail);
    if (filters.clientId) params.append('clientId', filters.clientId);
    if (filters.projectId) params.append('projectId', filters.projectId);
    if (filters.planId) params.append('planId', filters.planId);
    if (filters.includeUsageStats) params.append('includeUsageStats', filters.includeUsageStats);
    if (filters.minDr !== undefined) params.append('minDr', filters.minDr.toString());
    if (filters.maxDr !== undefined) params.append('maxDr', filters.maxDr.toString());
    if (filters.minTraffic !== undefined) params.append('minTraffic', filters.minTraffic.toString());
    if (filters.maxTraffic !== undefined) params.append('maxTraffic', filters.maxTraffic.toString());
    if (filters.minPrice !== undefined) params.append('minPrice', filters.minPrice.toString());
    if (filters.maxPrice !== undefined) params.append('maxPrice', filters.maxPrice.toString());
    if (filters.fromDate) params.append('fromDate', filters.fromDate);
    if (filters.toDate) params.append('toDate', filters.toDate);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const url = `/guest-sites?${params.toString()}`;
    const response = await apiService.get<PaginatedResponse<GuestBlogSite>>(url);
    return response;
  }

  private buildFilterParams(filters: GuestBlogSiteFilters = {}): URLSearchParams {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.category) params.append('category', filters.category);
    if (filters.country) params.append('country', filters.country);
    if (filters.siteLanguage) params.append('siteLanguage', filters.siteLanguage);
    if (filters.status) params.append('status', filters.status);
    if (filters.publisherId) params.append('publisherId', filters.publisherId);
    if (filters.outreachEmail) params.append('outreachEmail', filters.outreachEmail);
    if (filters.clientId) params.append('clientId', filters.clientId);
    if (filters.projectId) params.append('projectId', filters.projectId);
    if (filters.planId) params.append('planId', filters.planId);
    if (filters.minDr !== undefined) params.append('minDr', filters.minDr.toString());
    if (filters.maxDr !== undefined) params.append('maxDr', filters.maxDr.toString());
    if (filters.minTraffic !== undefined) params.append('minTraffic', filters.minTraffic.toString());
    if (filters.maxTraffic !== undefined) params.append('maxTraffic', filters.maxTraffic.toString());
    if (filters.minPrice !== undefined) params.append('minPrice', filters.minPrice.toString());
    if (filters.maxPrice !== undefined) params.append('maxPrice', filters.maxPrice.toString());
    if (filters.fromDate) params.append('fromDate', filters.fromDate);
    if (filters.toDate) params.append('toDate', filters.toDate);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    return params;
  }

  async exportGuestBlogSitesCsv(filters: GuestBlogSiteFilters = {}): Promise<{ blob: Blob; count: number }> {
    const params = this.buildFilterParams(filters);
    params.append('format', 'csv');
    const blob = await apiService.get<Blob>(`/guest-sites?${params.toString()}`, undefined, {
      timeout: 180000,
      responseType: 'blob',
    });
    const text = blob instanceof Blob ? await blob.text() : String(blob || '');
    const trimmed = text.trim();
    if (trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        throw new Error(parsed.error || parsed.message || 'Failed to export data');
      } catch (err) {
        if (err instanceof SyntaxError) {
          throw new Error('Failed to export data');
        }
        throw err;
      }
    }
    const count = trimmed
      ? Math.max(0, trimmed.replace(/\r\n/g, '\n').split('\n').filter((line) => line.trim()).length - 1)
      : 0;
    return {
      blob: new Blob([text], { type: 'text/csv;charset=utf-8;' }),
      count,
    };
  }

  async getGuestBlogSite(id: string, clientId?: string): Promise<ApiResponse<GuestBlogSite>> {
    const params = clientId ? `?clientId=${clientId}` : '';
    const response = await apiService.get<ApiResponse<GuestBlogSite>>(`/guest-sites/${id}${params}`);
    return response;
  }

  async createGuestBlogSite(data: CreateGuestBlogSiteRequest): Promise<ApiResponse<GuestBlogSite>> {
    console.log('Creating guest blog site with baseUrl:', this.baseUrl);
    const response = await apiService.post<ApiResponse<GuestBlogSite>>('/guest-sites', data);
    return response;
  }

  async updateGuestBlogSite(id: string, data: UpdateGuestBlogSiteRequest): Promise<ApiResponse<GuestBlogSite>> {
    const response = await apiService.put<ApiResponse<GuestBlogSite>>(`/guest-sites/${id}`, data);
    return response;
  }

  async deleteGuestBlogSite(id: string): Promise<ApiResponse<void>> {
    const response = await apiService.delete<ApiResponse<void>>(`/guest-sites/${id}`);
    return response;
  }

  async setPriceOverride(
    clientId: string,
    siteId: string,
    data: SetPriceOverrideRequest
  ): Promise<ApiResponse<void>> {
    const response = await apiService.post<ApiResponse<void>>(
      `/guest-sites/pricing/${clientId}/${siteId}/override`,
      data
    );
    return response;
  }

  async removePriceOverride(clientId: string, siteId: string): Promise<ApiResponse<void>> {
    const response = await apiService.delete<ApiResponse<void>>(
      `/guest-sites/pricing/${clientId}/${siteId}/override`
    );
    return response;
  }

  // Helper method to calculate displayed price
  calculateDisplayedPrice(basePrice: number, clientPercentage?: number): number {
    const percentage = clientPercentage || 100; // Default 100% markup
    return basePrice + (basePrice * percentage / 100);
  }

  // Helper method to format price
  formatPrice(price: number): string {
    return `$${price.toFixed(2)}`;
  }

  // Helper method to format traffic
  formatTraffic(traffic: number): string {
    if (traffic >= 1000000) return `${(traffic / 1000000).toFixed(1)}M`;
    if (traffic >= 1000) return `${(traffic / 1000).toFixed(1)}K`;
    return traffic.toString();
  }

  async checkAllSitesStatus(): Promise<{ success: boolean; message: string; totalSites: number; startedAt?: string | null }> {
    return apiService.post<{ success: boolean; message: string; totalSites: number; startedAt?: string | null }>(`${this.baseUrl}/check-all-status`, {});
  }

  /** Countries that have at least one visible site — for the catalog country filter. */
  async getAvailableCountries(): Promise<{ value: string; label: string; count: number }[]> {
    const response = await apiService.get<{
      success: boolean;
      data: { value: string; label: string; count: number }[];
    }>(`${this.baseUrl}/countries`);
    return response?.data || [];
  }

  /** Languages that have at least one active site — for filter dropdowns. */
  async getAvailableLanguages(): Promise<{ value: string; label: string; count: number }[]> {
    const response = await apiService.get<{
      success: boolean;
      data: { value: string; label: string; count: number }[];
    }>(`${this.baseUrl}/languages`);
    return response?.data || [];
  }
}

export const guestBlogSiteService = new GuestBlogSiteService();
